import * as logger from 'loglevel'

export function ensureChildAppsContainer(childAppsContainerRef, frameContentRef, eventId) {
  // First try the ref (set by AppDisplay render)
  if (childAppsContainerRef.current) {
    return childAppsContainerRef.current
  }

  // Ref not set yet - try to find by ID (container might exist from AppDisplay render but ref not set yet)
  const containerId = `anear-child-apps-container-${eventId}`
  let container = document.getElementById(containerId)

  if (container) {
    // Found by ID - set the ref so we use it next time
    childAppsContainerRef.current = container
    return container
  }

  // Container doesn't exist yet - create it if we have a parent to attach to
  if (frameContentRef.current && frameContentRef.current.parentNode) {
    container = document.createElement('div')
    container.id = containerId
    container.style.position = 'absolute'
    container.style.top = '0'
    container.style.left = '0'
    container.style.right = '0'
    container.style.bottom = '0'
    container.style.pointerEvents = 'none'
    container.style.zIndex = '0'
    frameContentRef.current.parentNode.appendChild(container)
    childAppsContainerRef.current = container
    logger.debug('EventFrame: Created child apps container as peer to frameContentRef')
    return container
  }

  // Can't create container yet - return null
  return null
}

// Move <anear-app> placeholders from the parent HTML into the overlay.
// An initialized host is reused so its shadow DOM survives the next parent render.
export function syncChildApps(frameContent, childAppsContainer, activeChildApps) {
  const newApps = Array.from(frameContent.querySelectorAll('anear-app'))
  const appsPresentInThisRender = new Set()

  newApps.forEach(newEl => {
    const appName = newEl.getAttribute('name')
    if (!appName) return

    // Track which apps are present in the current parent render. If an app is not present,
    // we will hide (but not destroy) its existing element in the child apps container.
    appsPresentInThisRender.add(appName)

    // Get attributes before removing from DOM
    const childEventId = newEl.getAttribute('data-child-event-id')

    // Check if this app already has an initialized element in the child apps container
    // If we find ANY initialized element for this app name, reuse it (parent HTML placeholders don't have child event IDs yet)
    const activeApps = activeChildApps.get(appName) || new Set()
    let existingElement = null

    // Look for an existing initialized element for this app
    // Prefer one with matching data-child-event-id if available, but any initialized element will work
    for (const el of activeApps) {
      if (el._initialized) {
        const existingId = el.getAttribute('data-child-event-id') || el._childEvent?.id
        // Match by ID if both have IDs, otherwise any initialized element for this app name works
        if (childEventId && existingId) {
          // Both have IDs - match by ID
          if (existingId === childEventId) {
            existingElement = el
            break
          }
        } else {
          // One or both don't have IDs - just use the first initialized element for this app
          existingElement = el
          break
        }
      }
    }

    if (existingElement) {
      // Element already exists in child apps container - just update its data-child-event-id if needed
      // Remove the placeholder newEl from parent HTML (don't move/create a new one)
      //
      // IMPORTANT: the placeholder element may carry updated attributes (class/options/style/etc)
      // that should be applied to the existing element (since the existing element persists
      // across parent HTML re-renders).
      newEl.remove()
      if (childEventId && !existingElement.getAttribute('data-child-event-id')) {
        existingElement.setAttribute('data-child-event-id', childEventId)
      }

      // Sync parent-provided attributes used for configuration/styling
      // (avoid overwriting identity/persistence attributes).
      try {
        const skipAttrs = new Set(['name', 'data-child-event-id'])

        // Apply/overwrite attrs present on the new placeholder
        for (const attr of Array.from(newEl.attributes)) {
          if (skipAttrs.has(attr.name)) continue
          existingElement.setAttribute(attr.name, attr.value)
        }

        // Remove attrs that were previously set but are no longer present on the placeholder
        // (keep only the ones that parents control, not internal bookkeeping).
        for (const attr of Array.from(existingElement.attributes)) {
          if (skipAttrs.has(attr.name)) continue
          if (!newEl.hasAttribute(attr.name)) {
            existingElement.removeAttribute(attr.name)
          }
        }
      } catch (e) {
        logger.warn('EventFrame: Failed syncing <anear-app> attributes from placeholder', e)
      }

      // Ensure visible if present in this render (a previous render may have hidden it).
      existingElement.style.removeProperty('display')
      existingElement.removeAttribute('data-parent-hidden')

      // Intentionally no log here; this is noisy in normal operation.
    } else {
      // New element - move it to child apps container (peer to parent content)
      // Remove from parent HTML first
      newEl.remove()
      childAppsContainer.appendChild(newEl)
      // Re-enable pointer events on the element itself (container has pointerEvents: none)
      newEl.style.pointerEvents = 'auto'
      // Ensure visible by default
      newEl.style.removeProperty('display')
      newEl.removeAttribute('data-parent-hidden')

      // Track this element
      if (!activeChildApps.has(appName)) {
        activeChildApps.set(appName, new Set())
      }
      activeChildApps.get(appName).add(newEl)

      logger.debug(`EventFrame: Moved <anear-app name="${appName}"> to child apps container (peer to parent content)`)
    }
  })

  // Hide apps that are NOT present in this parent render, but keep their Shadow DOM alive.
  // This allows temporary parent screens to omit the child app to reduce distraction.
  activeChildApps.forEach((activeApps, appName) => {
    if (appsPresentInThisRender.has(appName)) return
    activeApps.forEach(el => {
      if (!childAppsContainer.contains(el)) return
      el.style.display = 'none'
      el.setAttribute('data-parent-hidden', '1')
    })
  })

  // Clean up inactive apps
  // Only remove elements that are no longer in the DOM at all (completely removed from document)
  // Elements in the childAppsContainer should ALWAYS be kept (even if not initialized yet - initialization happens in useEffect)
  activeChildApps.forEach((activeApps, appName) => {
    activeApps.forEach(el => {
      // If element is in childAppsContainer, always keep it (initialization will happen separately)
      if (childAppsContainer.contains(el)) {
        // Element is properly placed in child container - keep it
        return
      }

      // Element is not in childAppsContainer - check if it still exists in DOM
      if (!document.body.contains(el)) {
        // Element completely removed from DOM - remove from tracking
        activeApps.delete(el)
        logger.debug(`EventFrame: Removed inactive <anear-app name="${appName}"> from tracking (no longer in DOM)`)
      }
      // If element exists in DOM but not in childAppsContainer, it might be orphaned
      // Keep it for now - next render cycle might fix it, or it might be in the process of being moved
    })
    if (activeApps.size === 0) {
      activeChildApps.delete(appName)
    }
  })
}

export function initializeTrackedChildApps(childAppsContainer, activeChildApps, anearEvent, anearParticipant, userAuth) {
  if (!childAppsContainer) return
  const allAnearApps = Array.from(childAppsContainer.querySelectorAll('anear-app'))

  allAnearApps.forEach((element) => {
    // Only initialize elements that belong to this parent event
    // Check if element is in one of our tracked app containers
    const appName = element.getAttribute('name')
    if (!appName) return

    const activeApps = activeChildApps.get(appName)
    if (!activeApps || !activeApps.has(element)) {
      // This element doesn't belong to our parent event, skip it
      return
    }

    // Check if element is already initialized
    if (element._initialized) {
      return // Already initialized, skip
    }

    // Initialize the AnearApp component with parent context
    // At this point, parent participant relationship is fully established
    if (typeof element.initialize === 'function') {
      element.initialize(anearEvent, anearParticipant, userAuth)
      logger.debug(`EventFrame: Initialized <anear-app name="${appName}"> at child apps container`)
    } else {
      logger.warn('AnearApp element found but initialize method not available')
    }
  })
}

export async function propagatePresenceEnter(activeChildApps, requiresGeoLocation) {
  for (const activeApps of activeChildApps.values()) {
    for (const element of activeApps) {
      // Only propagate if child event is already initialized (reconnect scenario)
      if (element._initialized && typeof element.propagatePresenceEnter === 'function') {
        try {
          await element.propagatePresenceEnter(requiresGeoLocation)
        } catch (err) {
          logger.error('EventFrame: Error propagating presence enter to child app on reconnect', err)
        }
      }
    }
  }
}

export async function propagateParticipantExit(activeChildApps) {
  for (const activeApps of activeChildApps.values()) {
    for (const element of activeApps) {
      if (element._initialized && typeof element.propagateExit === 'function') {
        try {
          await element.propagateExit('PARTICIPANT_EXIT')
        } catch (err) {
          logger.error('EventFrame: Error propagating exit to child app', err)
        }
      }
    }
  }
}
